import React, { useCallback } from 'react';

import { AIEventsInstrumentationProvider } from '@atlassian/ai-analytics';

import { useBulkCreateContext } from '../../providers/BulkCreateContextProvider';
import { AiBulkNavigationContainer } from '../../ui/ai-work-create/AiBulkNavigationContainer';
import { BulkIssueCreateProvidersWrapper } from '../../providers/BulkIssueCreateProvidersWrapper';
import { useGetInitialDataForBulkIssueCreateForm } from '../../services/useGetInitialDataForBulkIssueCreateForm';
import { useHasRequiredFields } from '../../services/useHasRequiredFields';
import { useGetWarningDialogActions } from '../../utils/useGetWarningDialogActions';
import { IssueCreateWarningDialog } from '../work-create-common/IssueCreateWarningDialog';
import {
	AI_BULK_ISSUE_CREATE_FEATURE_NAME,
	useBulkCreateLoggingEvents,
} from '../../utils/useBulkCreateLoggingEvents';

import { BulkNavigationContainer } from './BulkNavigationContainer';

export const bulkCreateSidePanelTestId = 'bulk-issue-create-side-panel';
export const BULK_ISSUE_CREATE_SIDE_PANEL_ID = 'bulk-issue-create-side-panel';
export const BULK_ISSUE_CREATE_SIDE_PANEL_WIDTH = 320;

export const BulkCreateSidePanelComponent = ({ onClose }: { onClose: () => void }) => {
	useGetInitialDataForBulkIssueCreateForm();
	const [state] = useBulkCreateContext();
	const { isCreatedWithAi, bulkIssuesList } = state;
	useHasRequiredFields();
	const { logSidePanelClosed } = useBulkCreateLoggingEvents();

	const handleOnClose = useCallback(() => {
		logSidePanelClosed();
		onClose();
	}, [logSidePanelClosed, onClose]);

	const { isWarningDialogOpen, handleConfirm, handleCancel } =
		useGetWarningDialogActions(handleOnClose);

	return (
		<>
			{isCreatedWithAi ? (
				<AiBulkNavigationContainer onClose={handleOnClose} />
			) : (
				<BulkNavigationContainer onClose={handleOnClose} />
			)}
			{isWarningDialogOpen && (
				<IssueCreateWarningDialog
					onCancel={handleCancel}
					onConfirm={handleConfirm}
					issuesCount={bulkIssuesList?.length}
				/>
			)}
		</>
	);
};

export const BulkCreateSidePanel = ({ onClose }: { onClose: () => void }) => {
	return (
		<BulkIssueCreateProvidersWrapper>
			<AIEventsInstrumentationProvider
				config={{
					// Required configruation
					product: 'confluence',
					subproduct: 'jira',
					aiFeatureName: AI_BULK_ISSUE_CREATE_FEATURE_NAME,
					proactiveGeneratedAI: 0,
					userGeneratedAI: 1,
					// Optional configuration
					source: 'confluenceHighlightIssueCreate',
					skipAISessionValidation: true,
				}}
			>
				<BulkCreateSidePanelComponent onClose={onClose} />
			</AIEventsInstrumentationProvider>
		</BulkIssueCreateProvidersWrapper>
	);
};
